import {reportIpmAction, type IpmMetricTags} from '@github-ui/ipm-telemetry'
import {Button, Link} from '@primer/react'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {announce} from '@github-ui/aria-live'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {IpmBannerBase} from '../ipm-banner-base/IpmBannerBase'

export interface CopilotBudgetRequestBannerAnalytics {
  visibleEvent: string
  dismissEvent: string
  clickEvent: string
  location: string
}

export type BannerState = 'pre_request' | 'post_request' | 'request_denied'

export type BudgetScope = 'organization' | 'enterprise' | 'cost_center'

interface BudgetTermCopy {
  label: string
  title: string
  personalExhausted: string
  accountExhausted: string
}

const LEGACY_BUDGET_TERM_COPY: BudgetTermCopy = {
  label: 'Copilot budget',
  title: 'Copilot budget exhausted',
  personalExhausted: "You've used your full Copilot budget",
  accountExhausted: 'has used its full Copilot budget',
}

const AI_CREDITS_BUDGET_TERM_COPY: BudgetTermCopy = {
  label: 'AI credits',
  title: 'AI credits exhausted',
  personalExhausted: "You've used all your AI credits",
  accountExhausted: 'has used all its AI credits',
}

// Keep the previous key until the backend rollout is complete.
const BUDGET_TERM_COPY_BY_KEY: Record<string, BudgetTermCopy> = {
  ai_credits: AI_CREDITS_BUDGET_TERM_COPY,
  premium_requests: AI_CREDITS_BUDGET_TERM_COPY,
}

const DEFAULT_APPROVERS = 'org admins'
const DEFAULT_APPROVER = 'an admin'

// Who can grant more budget, keyed on the budget's scope. Enterprise-scoped
// budgets point the member at their enterprise account owners; other scopes use
// the org-admin wording. Keying it here keeps future scope-specific changes to a
// one-line edit rather than a new set of string literals.
const APPROVERS_BY_SCOPE: Record<BudgetScope, string> = {
  organization: DEFAULT_APPROVERS,
  enterprise: 'enterprise account owners',
  cost_center: DEFAULT_APPROVERS,
}

// Singular approver wording (e.g. "declined by an admin"), keyed on the same scope.
const APPROVER_BY_SCOPE: Record<BudgetScope, string> = {
  organization: DEFAULT_APPROVER,
  enterprise: 'an enterprise account owner',
  cost_center: DEFAULT_APPROVER,
}

export interface CopilotBudgetRequestBannerProps {
  isPersonalBudget: boolean
  // The budget's scope, sourced from real budget data. Optional during rollout:
  // old-shape cache entries omit it, so callers may not provide it yet. The
  // `enterprise` scope also drives the enterprise-specific approver wording and
  // surfaces the enterprise (not org) as the budget source.
  scope?: BudgetScope
  // The backend-resolved product term key. Optional during rollout; accepts the
  // previous "premium_requests" key and falls back to legacy copy when absent.
  budgetTerm?: string
  organizationName: string
  enterpriseName?: string
  resetDate: string
  requestUrl: string
  requestedAmountCents: number
  /**
   * Login of the organization the request is associated with. Required when
   * `requestUrl` points at an enterprise-scoped endpoint
   * (`/enterprises/:slug/copilot/budget_request`), where the server cannot
   * infer the organization from the URL. When set, it is sent as
   * `organization` in the request body. Omit for org-scoped requests.
   */
  organizationLogin?: string
  removeRequestUrl?: string
  contactUrl: string
  dismissPaths: Record<BannerState, string>
  bannerState: BannerState
  declinedDate?: string
  analytics: CopilotBudgetRequestBannerAnalytics
  rounded?: boolean
}

// Append the interaction's page + element to a URL's query string. They ride on the query
// (not a JSON body) because the backend reads params[:display_location]/[:surface] in its
// instrumentation around-action, before parse_json_params merges the request's JSON body.
function appendInteractionTags(url: string, tags: IpmMetricTags): string {
  const query = new URLSearchParams()
  if (tags.display_location) query.set('display_location', tags.display_location)
  if (tags.surface) query.set('surface', tags.surface)
  const queryString = query.toString()
  return queryString ? `${url}${url.includes('?') ? '&' : '?'}${queryString}` : url
}

export function CopilotBudgetRequestBanner({
  isPersonalBudget,
  scope,
  budgetTerm,
  organizationName,
  enterpriseName,
  resetDate,
  requestUrl,
  requestedAmountCents,
  organizationLogin,
  removeRequestUrl,
  contactUrl,
  dismissPaths,
  bannerState: initialBannerState,
  declinedDate,
  analytics,
  rounded = false,
}: CopilotBudgetRequestBannerProps) {
  const [bannerState, setBannerState] = useState<BannerState>(initialBannerState)
  const [submitting, setSubmitting] = useState(false)
  const bannerRef = useRef<HTMLDivElement>(null)
  const isInitialRenderRef = useRef(true)
  const dismissPath = dismissPaths[bannerState]

  // Product term + approver wording, driven by real budget data when present and
  // shimmed to the legacy wording when the backend has not sent it yet. Enterprise-
  // scoped budgets point the member at their enterprise account owners and surface
  // the enterprise (not org) as the budget source.
  const budgetTermCopy = budgetTerm
    ? (BUDGET_TERM_COPY_BY_KEY[budgetTerm] ?? LEGACY_BUDGET_TERM_COPY)
    : LEGACY_BUDGET_TERM_COPY
  const approvers = scope ? APPROVERS_BY_SCOPE[scope] : DEFAULT_APPROVERS
  const approver = scope ? APPROVER_BY_SCOPE[scope] : DEFAULT_APPROVER
  const budgetSourceName = scope === 'enterprise' && enterpriseName ? enterpriseName : organizationName
  const isEnterpriseOwnedBudget = (scope === 'enterprise' || scope === 'cost_center') && Boolean(enterpriseName)
  const accountBudgetOwnerLabel = isEnterpriseOwnedBudget ? 'enterprise' : 'organization'
  const accountBudgetOwnerName = isEnterpriseOwnedBudget && enterpriseName ? enterpriseName : organizationName
  const telemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'in-product-targeting',
      component: 'CopilotBudgetRequestBanner',
      surface: 'copilot_budget_request_banner',
      display_location: analytics.location,
      category: 'copilot_budget_request_banner',
      nudge_id: 'copilot_premium_request_budget',
      variant: bannerState,
    }),
    [analytics.location, bannerState],
  )

  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false
      return
    }
    bannerRef.current?.focus()
  }, [bannerState])

  const handleRequestClick = useCallback(async () => {
    reportIpmAction('cta_click', telemetryTags)
    setSubmitting(true)
    try {
      const response = await verifiedFetch(appendInteractionTags(requestUrl, telemetryTags), {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          requested_amount_cents: requestedAmountCents,
          ...(organizationLogin ? {organization: organizationLogin} : {}),
        }),
      })
      if (response.ok) {
        setBannerState('post_request')
        announce(`Request sent. Your ${approvers} will be notified.`)
      }
    } catch {
      reportIpmAction('error', telemetryTags, {samplingProbability: 1})
      // Network error — keep current state
    } finally {
      setSubmitting(false)
    }
  }, [requestUrl, requestedAmountCents, telemetryTags, organizationLogin, approvers])

  const handleRemoveRequestClick = useCallback(async () => {
    if (!removeRequestUrl) return
    reportIpmAction('cta_click', telemetryTags)
    setSubmitting(true)
    try {
      const response = await verifiedFetch(appendInteractionTags(removeRequestUrl, telemetryTags), {method: 'DELETE'})
      if (response.ok) {
        setBannerState('pre_request')
        announce('Request removed.')
      }
    } catch {
      reportIpmAction('error', telemetryTags, {samplingProbability: 1})
      // Network error — keep current state
    } finally {
      setSubmitting(false)
    }
  }, [removeRequestUrl, telemetryTags])

  if (bannerState === 'post_request') {
    return (
      <IpmBannerBase
        ref={bannerRef}
        dismissPath={dismissPath}
        visibleEvent={analytics.visibleEvent}
        dismissEvent={analytics.dismissEvent}
        testId="copilot-budget-request-banner"
        title="Request sent"
        variant="success"
        location={analytics.location}
        rounded={rounded}
        telemetryTags={telemetryTags}
        description={
          <span className="text-normal">
            <strong>Request sent.</strong> Your {approvers} will be notified that you need additional{' '}
            {budgetTermCopy.label}.
            {removeRequestUrl && (
              <>
                <br />
                <span className="d-inline-flex flex-items-center mt-1">
                  <Button
                    variant="primary"
                    size="small"
                    onClick={handleRemoveRequestClick}
                    disabled={submitting}
                    data-analytics-event={analytics.clickEvent}
                  >
                    Remove request
                  </Button>
                  <span className="ml-2">You can re-request after this is reviewed.</span>
                </span>
              </>
            )}
          </span>
        }
      />
    )
  }

  if (bannerState === 'request_denied') {
    return (
      <IpmBannerBase
        ref={bannerRef}
        dismissPath={dismissPath}
        visibleEvent={analytics.visibleEvent}
        dismissEvent={analytics.dismissEvent}
        testId="copilot-budget-request-banner"
        title="Budget request declined"
        variant="warning"
        location={analytics.location}
        rounded={rounded}
        telemetryTags={telemetryTags}
        description={
          <span className="text-normal">
            {declinedDate ? (
              <span>
                {`Your request for additional ${budgetTermCopy.label} was declined by ${approver} on `}
                <strong>{declinedDate}</strong>
                {'. '}
              </span>
            ) : (
              <span>{`Your request for additional ${budgetTermCopy.label} was declined by ${approver}. `}</span>
            )}
            <Link
              href={contactUrl}
              inline
              className="Link--inTextBlock"
              onClick={() => reportIpmAction('click', telemetryTags)}
            >
              {`Contact ${approver}`}
            </Link>{' '}
            <span>for more details.</span>
          </span>
        }
      />
    )
  }

  // pre_request state
  if (isPersonalBudget) {
    return (
      <IpmBannerBase
        ref={bannerRef}
        dismissPath={dismissPath}
        visibleEvent={analytics.visibleEvent}
        dismissEvent={analytics.dismissEvent}
        testId="copilot-budget-request-banner"
        title={budgetTermCopy.title}
        variant="critical"
        location={analytics.location}
        rounded={rounded}
        telemetryTags={telemetryTags}
        description={
          <span className="text-normal">
            {budgetTermCopy.personalExhausted} from <strong>{budgetSourceName}</strong>. Access resumes on {resetDate}.
            Your {approvers} can increase it sooner.
          </span>
        }
        primaryAction={
          <Button onClick={handleRequestClick} disabled={submitting} data-analytics-event={analytics.clickEvent}>
            {submitting ? 'Submitting...' : 'Request more budget'}
          </Button>
        }
      />
    )
  }

  return (
    <IpmBannerBase
      ref={bannerRef}
      dismissPath={dismissPath}
      visibleEvent={analytics.visibleEvent}
      dismissEvent={analytics.dismissEvent}
      testId="copilot-budget-request-banner"
      title={budgetTermCopy.title}
      variant="critical"
      location={analytics.location}
      rounded={rounded}
      telemetryTags={telemetryTags}
      description={
        <span className="text-normal">
          Your {accountBudgetOwnerLabel} <strong>{accountBudgetOwnerName}</strong> {budgetTermCopy.accountExhausted}.
          Access resumes on {resetDate}. Contact your admins to increase it.
        </span>
      }
    />
  )
}
